import type { AcceptedTrayTypes, IsTrayType } from "./types";

/**
 * German tray vocabulary, ported from the agent's `tray_classify.decide_locally`
 * (which composes `plan_strategy._TRAY_RE` / `_NOT_TRAY_RE`).
 *
 * The annotation has no tray object — it has a polyline with a free-text label,
 * and a cable tray is only one of the things a surveyor draws with one. Across
 * the whole asset bucket, 18,057 drawn routes on 520 plans, only 19% of the
 * drawn metres carry tray vocabulary; the rest are supply boundaries, core
 * drills, placeholders and prose lifted off the legend. Route cable along all
 * of them and a circuit rides a hole in a wall.
 *
 * Kept deliberately as a COPY of the agent's, not a reinterpretation: the two
 * must answer the same, and the agent's list is the one measured against real
 * plans. Retire this when the run publishes its verdicts as data (Stage 5) —
 * today it publishes only the already-filtered network, which cannot say
 * whether a type was rejected or simply never seen.
 */
const TRAY =
  /kabelkanal|kabeltrasse|kabeltragsystem|gitterrinne|kabelleiter|bodenkanal|br[uü]stungskanal|sockelkanal|kabelpritsche|steigtrasse|installationskanal|verlege\s*-?\s*trasse|kabelrinne|kabelb[uü]hne|kabelbuehne|gitterbahn|steigetrasse|sammelhalter/i;

/**
 * NOT a tray. This wins over `TRAY`, because "Platzhalter möglicher Kabelwege im
 * Doppelboden" contains a tray word and is a placeholder for one.
 *
 * The earthing terms are an addition to the agent's list, and they are a domain
 * claim worth stating: a Ringerder, Fundamenterder or Potentialausgleich is a
 * CONDUCTOR — buried in the foundation or bonded to steel — not a support
 * system anything else can be laid in. Routing a power circuit along the earth
 * ring is what put a cable across the floor of project 642631.
 */
const NOT_TRAY =
  /versorgungsgrenze|unbekannt|brandschutz|profilschiene|neubau|befestigung|h[oö]henversprung|montagewinkel|platzhalter|kernlochbohrung|kernbohrung|brandschott|durchbruch|bauabschnitt|versorgungsbereich|[a-zäöüß]*grenzen?\b|leerrohr|kabuflex|ringerder|fundamenterder|potentialausgleich|erdungsleiter/i;

export const isTrayType: IsTrayType = (label) => {
  const text = (label ?? "").trim();
  if (!text) return false;
  if (NOT_TRAY.test(text)) return false;
  if (TRAY.test(text)) return true;
  return null;
};

/**
 * Which drawn route types cable may actually be laid in.
 *
 * The RUN's verdict wins wherever it has one. It classified every type it saw —
 * deterministically where the label settled it, with one model call for the
 * ambiguous middle — and published the result as the network it filtered down
 * to. A type in that network is a tray, decided.
 *
 * What the run cannot tell us apart is a type it REJECTED from one it never
 * saw, because only the survivors are published. So a type absent from its
 * network is admitted only when the vocabulary positively says tray — which is
 * what lets an estimator draw a new Kabelrinne today and have it carry cable,
 * without re-admitting the Versorgungsgrenze the run already threw out.
 *
 * A plan the run published no tray network for falls back to the vocabulary
 * alone: there is no verdict to defer to, and refusing everything would route
 * a whole plan across open floor.
 */
export const acceptedTrayTypes: AcceptedTrayTypes = ({
  drawnTypes,
  runAcceptedTypes,
  chosen,
}) => {
  const fromRun = new Set(runAcceptedTypes.filter(Boolean));
  const byDefault = (type: string): boolean =>
    fromRun.size === 0
      ? isTrayType(type) === true
      : fromRun.has(type) || isTrayType(type) === true;

  const out = new Set<string>();
  // The run's own network can name a type nothing on the plan is drawn in any
  // more — a rename, or a re-run. It stays admitted: it was a verdict about
  // steel, and dropping it here would silently change what a stored figure was
  // measured over.
  for (const type of fromRun) if (chosen?.[type] !== false) out.add(type);

  for (const type of drawnTypes) {
    // HIS verdict first, in both directions. Everything below it is a guess
    // about a label; this is the man who drew it saying what it is.
    const said = chosen?.[type];
    if (said === true) {
      out.add(type);
      continue;
    }
    if (said === false) {
      out.delete(type);
      continue;
    }
    if (byDefault(type)) out.add(type);
  }
  return out;
};
